// /Users/aminajackson/Git-projects/pro-maximizer/frontend/src/BoardsPage.tsx

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  Avatar,
  Menu,
  Tooltip,
  CardHeader,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { Project } from './App'; // FIX: Import the Project type from its new location in App.tsx

import { API_BASE_URL } from './api';

// --- Data Interfaces ---
interface User {
  id: string;
  name: string;
  avatar: string; // Could be an initial or a URL
}

interface BoardCard {
  id: string;
  title: string;
  content: string;
  assignee?: User;
}

interface BoardColumn {
  id: string;
  title: string;
  cards: BoardCard[];
}

interface BoardsPageProps {
  projects: Project[];
}

interface FetchedItem {
  title: string;
  description: string;
}

// --- Prop interfaces for sub-components ---
interface BoardViewProps {
  boardName: string;
  columns: BoardColumn[];
  setColumns: React.Dispatch<React.SetStateAction<BoardColumn[]>>;
  setView: React.Dispatch<React.SetStateAction<'creation' | 'board'>>;
}

interface BoardCreationFormProps {
  projects: Project[];
  setView: React.Dispatch<React.SetStateAction<'creation' | 'board'>>;
  setBoardName: React.Dispatch<React.SetStateAction<string>>;
  setColumns: React.Dispatch<React.SetStateAction<BoardColumn[]>>;
}


// --- Mock Data & Configuration ---
const MOCK_USERS: User[] = [
  { id: 'user1', name: 'Amina Jackson', avatar: 'AJ' },
  { id: 'user2', name: 'Leo Valdez', avatar: 'LV' },
  { id: 'user3', name: 'Mei Lin', avatar: 'ML' },
];

const importFields: { [key: string]: any[] } = {
  jira: [
    { name: 'instanceUrl', label: 'Jira Instance URL', type: 'text', placeholder: 'https://your-company.atlassian.net' },
    { name: 'email', label: 'Atlassian Account Email', type: 'email', placeholder: 'user@example.com' },
    { name: 'projectKey', label: 'Project Key', type: 'text', placeholder: 'PROJ' },
    { name: 'apiToken', label: 'API Token', type: 'password', helperText: 'Create an API token from your Atlassian account settings.' },
  ],
  ado: [
    { name: 'orgUrl', label: 'Azure DevOps Organization URL', type: 'text', placeholder: 'https://dev.azure.com/your-org' },
    { name: 'projectName', label: 'Project Name', type: 'text' },
    { name: 'pat', label: 'Personal Access Token (PAT)', type: 'password', helperText: 'Create a PAT with "Work Items - Read" permissions.' },
  ],
};

// --- Sub-components for better organization ---

/**
 * Renders the main board view with columns and cards.
 */
const BoardView: React.FC<BoardViewProps> = ({ boardName, columns, setColumns, setView }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCard, setSelectedCard] = useState<{ colId: string; cardId: string } | null>(null);

  const handleAssigneeMenuClick = (event: React.MouseEvent<HTMLElement>, colId: string, cardId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedCard({ colId, cardId });
  };

  const handleAssigneeMenuClose = () => {
    setAnchorEl(null);
    setSelectedCard(null);
  };

  const handleAssignUser = (user: User | null) => {
    if (!selectedCard) return;

    const { colId, cardId } = selectedCard;
    const newColumns = columns.map((col: BoardColumn) => {
      if (col.id === colId) {
        return {
          ...col,
          cards: col.cards.map((card: BoardCard) =>
            card.id === cardId ? { ...card, assignee: user || undefined } : card
          ),
        };
      }
      return col;
    });
    setColumns(newColumns);
    handleAssigneeMenuClose();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">{boardName}</Typography>
        <Button onClick={() => setView('creation')}>Create New Board</Button>
      </Box>
      <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', p: 1, pb: 3 }}>
        {columns.map((col: BoardColumn) => (
          <Paper key={col.id} sx={{ p: 2, minWidth: 320, flexShrink: 0, bgcolor: 'grey.100' }}>
            <Typography variant="h6" gutterBottom>{col.title}</Typography>
            <Stack spacing={2}>
              {col.cards.map((card: BoardCard) => (
                <Card key={card.id} elevation={1}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle1" sx={{ mb: 1, pr: 2 }}>{card.title}</Typography>
                      <Tooltip title={card.assignee ? `Assigned to ${card.assignee.name}` : 'Unassigned'}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: card.assignee ? 'primary.main' : 'grey.400',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                          }}
                          onClick={(e) => handleAssigneeMenuClick(e, col.id, card.id)}
                        >
                          {card.assignee ? card.assignee.avatar : <PersonAddIcon />}
                        </Avatar>
                      </Tooltip>
                    </Box>
                    <Typography variant="body2" color="text.secondary">{card.content}</Typography>
                    <Box sx={{ textAlign: 'right', mt: 1 }}>
                      <IconButton size="small"><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small"><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              <Button startIcon={<AddIcon />} size="small">Add Card</Button>
            </Stack>
          </Paper>
        ))}
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleAssigneeMenuClose}
      >
        <MenuItem onClick={() => handleAssignUser(null)}><em>Unassigned</em></MenuItem>
        {MOCK_USERS.map(user => (
          <MenuItem key={user.id} onClick={() => handleAssignUser(user)}>
            <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.7rem' }}>{user.avatar}</Avatar>
            {user.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

/**
 * Renders the form for creating a new board.
 */
const BoardCreationForm: React.FC<BoardCreationFormProps> = ({ projects, setView, setBoardName, setColumns }) => {
  const [formState, setFormState] = useState({
    boardName: '',
    boardType: 'kanban' as 'kanban' | 'agile' | 'custom',
    customColumns: 'To Do, In Progress, Done',
    populationMethod: 'scratch' as 'autogenerate' | 'import' | 'scratch',
    selectedProjectId: '',
    importService: '' as 'jira' | 'ado' | '',
    importCredentials: {} as { [key: string]: string },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importConnection, setImportConnection] = useState<{ status: 'success' | 'error', message: string } | null>(null);
  const [fetchedImportItems, setFetchedImportItems] = useState<FetchedItem[]>([]);

  const handleFormChange = (field: string, value: any) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    // Reset dependent state when a major option changes
    if (field === 'populationMethod') {
      setImportConnection(null);
      setFetchedImportItems([]);
      setFormState(prev => ({ ...prev, importService: '', importCredentials: {} }));
    }
    if (field === 'importService') {
      setImportConnection(null);
      setFetchedImportItems([]);
      setFormState(prev => ({ ...prev, importCredentials: {} }));
    }
  };

  const handleTestAndFetchImport = async () => {
    if (!formState.importService) return;
    setIsLoading(true);
    setError(null);
    setImportConnection(null);
    try {
      const response = await fetch(`${API_BASE_URL}/import/${formState.importService}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formState.importCredentials),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || `Failed to connect to ${formState.importService.toUpperCase()}.`);

      setFetchedImportItems(data.items);
      setImportConnection({ status: 'success', message: `Successfully connected and fetched ${data.items.length} items.` });
    } catch (err: any) {
      setImportConnection({ status: 'error', message: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBoard = async () => {
    if (!formState.boardName) {
      setError('Board Name is required.');
      return;
    }
    if (formState.populationMethod === 'import' && fetchedImportItems.length === 0) {
      setError('Please connect and fetch items before creating the board.');
      return;
    }

    setIsLoading(true);
    setError(null);

    let initialColumns: BoardColumn[];
    if (formState.boardType === 'kanban') {
      initialColumns = [{ id: 'todo', title: 'To Do', cards: [] }, { id: 'inprogress', title: 'In Progress', cards: [] }, { id: 'done', title: 'Done', cards: [] }];
    } else if (formState.boardType === 'agile') {
      initialColumns = [{ id: 'backlog', title: 'Backlog', cards: [] }, { id: 'sprint', title: 'Current Sprint', cards: [] }, { id: 'inreview', title: 'In Review', cards: [] }, { id: 'complete', title: 'Complete', cards: [] }];
    } else {
      initialColumns = formState.customColumns.split(',').map(c => ({ id: c.trim().toLowerCase().replace(/ /g, '-'), title: c.trim(), cards: [] }));
    }

    try {
      let newCards: BoardCard[] = [];
      if (formState.populationMethod === 'autogenerate' && formState.selectedProjectId) {
        const response = await fetch(`${API_BASE_URL}/project/generate-board-features`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_id: formState.selectedProjectId, methodology: formState.boardType }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Failed to generate features.');
        newCards = data.features.map((f: { title: string; description: string }, i: number) => ({ id: `card-${Date.now()}-${i}`, title: f.title, content: f.description }));
      } else if (formState.populationMethod === 'import') {
        newCards = fetchedImportItems.map((item, i) => ({ id: `card-${Date.now()}-${i}`, title: item.title, content: item.description }));
      }
      if (newCards.length > 0) {
        initialColumns[0].cards = newCards;
      }
    } catch (err: any) {
      setError(err.message);
      setIsLoading(false);
      return;
    }

    setBoardName(formState.boardName);
    setColumns(initialColumns);
    setView('board');
    setIsLoading(false);
  };

  const isImportConnectDisabled = useMemo(() => {
    if (!formState.importService) return true;
    const required = importFields[formState.importService];
    return required.some(field => !formState.importCredentials[field.name]);
  }, [formState.importService, formState.importCredentials]);

  return (
    <Paper sx={{ p: 4, maxWidth: 900, margin: 'auto' }}>
      <Typography variant="h4" gutterBottom>Create a New Board</Typography>

      <Typography variant="h6" sx={{ mt: 3 }}>1. Board Setup</Typography>
      <TextField fullWidth label="Board Name" value={formState.boardName} onChange={(e) => handleFormChange('boardName', e.target.value)} margin="normal" />
      <FormControl margin="normal">
        <Typography variant="subtitle2" gutterBottom>Board Type</Typography>
        <ToggleButtonGroup value={formState.boardType} exclusive onChange={(_, v) => v && handleFormChange('boardType', v)} color="primary">
          <ToggleButton value="kanban">Kanban</ToggleButton>
          <ToggleButton value="agile">Agile/Scrum</ToggleButton>
          <ToggleButton value="custom">Custom</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      {formState.boardType === 'custom' && (
        <TextField fullWidth label="Custom Column Names" helperText="Enter column names, separated by commas" value={formState.customColumns} onChange={(e) => handleFormChange('customColumns', e.target.value)} margin="normal" />
      )}

      <Typography variant="h6" sx={{ mt: 4 }}>2. Populate Board</Typography>
      <ToggleButtonGroup orientation="vertical" value={formState.populationMethod} exclusive onChange={(_, v) => v && handleFormChange('populationMethod', v)} fullWidth sx={{ mt: 2 }}>
        <ToggleButton value="autogenerate" sx={{ justifyContent: 'flex-start', p: 2 }}>
          <Stack><Typography><b>Autogenerate from Project Plan</b></Typography><Typography variant="body2">Use AI to create stories/tasks from an existing project.</Typography></Stack>
        </ToggleButton>
        <ToggleButton value="import" sx={{ justifyContent: 'flex-start', p: 2 }}>
          <Stack><Typography><b>Import from External Service</b></Typography><Typography variant="body2">Connect to Jira or Azure DevOps to import work items.</Typography></Stack>
        </ToggleButton>
        <ToggleButton value="scratch" sx={{ justifyContent: 'flex-start', p: 2 }}>
          <Stack><Typography><b>Start from Scratch</b></Typography><Typography variant="body2">Create an empty board and add items manually.</Typography></Stack>
        </ToggleButton>
      </ToggleButtonGroup>

      {formState.populationMethod === 'autogenerate' && (
        <FormControl fullWidth margin="normal">
          <InputLabel>Select Project to Generate From</InputLabel>
          <Select value={formState.selectedProjectId} label="Select Project to Generate From" onChange={(e) => handleFormChange('selectedProjectId', e.target.value)}>
            {projects.map((p: Project) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      {formState.populationMethod === 'import' && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, borderColor: 'primary.main' }}>
          <Typography variant="subtitle1" gutterBottom>Import Configuration</Typography>
          <FormControl margin="normal">
            <Typography variant="subtitle2" gutterBottom>Select Service</Typography>
            <ToggleButtonGroup value={formState.importService} exclusive onChange={(_, v) => v && handleFormChange('importService', v)} color="primary">
              <ToggleButton value="jira">Jira</ToggleButton>
              <ToggleButton value="ado">Azure DevOps</ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          {importFields[formState.importService]?.map(field => (
            <TextField key={field.name} name={field.name} label={field.label} type={field.type} value={formState.importCredentials[field.name] || ''} onChange={(e) => handleFormChange('importCredentials', { ...formState.importCredentials, [e.target.name]: e.target.value })} fullWidth margin="normal" placeholder={field.placeholder} helperText={field.helperText} />
          ))}

          {formState.importService && (
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={handleTestAndFetchImport} disabled={isLoading || isImportConnectDisabled}>
                {isLoading ? <CircularProgress size={24} /> : 'Connect & Fetch Items'}
              </Button>
              {importConnection && (
                <Alert severity={importConnection.status} icon={importConnection.status === 'success' ? <CheckCircleIcon /> : <ErrorIcon />} sx={{ mt: 2 }}>
                  {importConnection.message}
                </Alert>
              )}
            </Box>
          )}
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Box sx={{ mt: 4, textAlign: 'right' }}>
        <Button variant="contained" size="large" onClick={handleCreateBoard} disabled={isLoading}>
          {isLoading ? <CircularProgress size={24} /> : 'Create Board'}
        </Button>
      </Box>
    </Paper>
  );
};

/**
 * Main component orchestrator.
 */
const BoardsPage: React.FC<BoardsPageProps> = ({ projects }) => {
  const [view, setView] = useState<'creation' | 'board'>('creation');
  const [boardName, setBoardName] = useState('');
  const [columns, setColumns] = useState<BoardColumn[]>([]);

  if (view === 'creation') {
    return <BoardCreationForm projects={projects} setView={setView} setBoardName={setBoardName} setColumns={setColumns} />;
  }

  return <BoardView boardName={boardName} columns={columns} setColumns={setColumns} setView={setView} />;
};

export default BoardsPage;